import { IndexedDbMiningCheckpointStore } from "./checkpoint-store";
import { normalizeWorkload } from "./workload";
import { cpuCapacity, MAX_CPU_WORKERS, validateCpuWorkers } from "./cpu-capacity";
import { estimateMiningTimeMs } from "./estimate";
import {
  MAX_UINT256,
  challengeCheckpointKey,
  normalizeChallenge,
  verifyPowCandidate,
  parseUint256,
} from "./pow-v1";
import { BrowserMiningTabLease } from "./tab-lease";
import { ServerMiningLease, sessionWire, type MiningAllocationPolicy } from "./server-lease";
import type { MiningSessionApi } from "../api/mining-session";
import type { WalletSessionSnapshot } from "../wallet/wallet-session";
import type {
  BatchResult,
  GpuCapability,
  Hex,
  LocalPowCandidate,
  MiningApi,
  MiningBackend,
  MiningBackendFactories,
  MiningChallenge,
  MiningChallengeWire,
  MiningCheckpoint,
  MiningCheckpointStore,
  MiningMode,
  MiningPowerMode,
  MiningProof,
  MiningSnapshot,
  MiningTabLease,
} from "./types";
import { createCpuWorkerBackend, createGpuWorkerBackend } from "./worker-backend";

export type MiningControllerConfiguration = {
  api?: MiningApi;
  challenge?: MiningChallengeWire;
  /** Fetch and restore this server challenge when the full response is not already available. */
  challengeId?: Hex;
  guard?: MiningStartOptions["guard"];
};

export type MiningControllerOptions = {
  server?: { api: MiningSessionApi; auth(): WalletSessionSnapshot; allocationPolicy: MiningAllocationPolicy | null; workerId?: string };
  api: MiningApi;
  checkpointStore?: MiningCheckpointStore;
  tabLease?: MiningTabLease;
  backends?: MiningBackendFactories;
  nowMs?: () => number;
  /** Opt-in policy; ordinary tab switches keep CPU/GPU mining active by default. */
  autoPauseWhenHidden?: boolean;
};

export type MiningStartOptions = {
  takeover?: boolean;
  freshSearch?: boolean;
  mode?: MiningMode;
  powerMode?: MiningPowerMode;
  guard?: { signal: AbortSignal; assertCurrent(): void };
};

export interface MiningController {
  discover?(token: string, guard: NonNullable<MiningStartOptions["guard"]>): Promise<void>;
  createAttempt?(token: string, guard: NonNullable<MiningStartOptions["guard"]>): Promise<void>;
  checkSavedProof?(id: Hex, guard: NonNullable<MiningStartOptions["guard"]>, token?: string): Promise<void>;
  subscribe(listener: () => void): () => void;
  getSnapshot(): MiningSnapshot;
  configure(configuration: MiningControllerConfiguration): Promise<void>;
  restore(context: { walletAddress: string; chainId: string; tokenAddress: string },
    guard: NonNullable<MiningStartOptions["guard"]>): Promise<boolean>;
  retireProof(proofId: string, status: "EXPIRED" | "CONSUMED"): void;
  start(options?: MiningStartOptions): Promise<void>;
  pause(reason?: string): Promise<void>;
  resume(options?: MiningStartOptions): Promise<void>;
  stop(): Promise<void>;
  retrySubmission(options?: MiningStartOptions): Promise<void>;
  setMode(mode: MiningMode): Promise<void>;
  setPowerMode(mode: MiningPowerMode): Promise<void>;
  setCpuWorkers(count: number): Promise<void>;
  dispose(): Promise<void>;
}

const DEFAULT_GPU: GpuCapability = {
  status: "UNKNOWN", reason: null, adapterInfo: null, selfTested: false,
};
const MAX_LOGS = 60;

export class BrowserMiningController implements MiningController {
  private readonly server: ServerMiningLease | null;
  private discovery = 0;
  private recoveryPromise: Promise<void> | null = null;
  private api: MiningApi;
  private readonly store: MiningCheckpointStore;
  private readonly lease: MiningTabLease;
  private readonly factories: MiningBackendFactories;
  private readonly nowMs: () => number;
  private readonly listeners = new Set<() => void>();
  private snapshot: MiningSnapshot = initialSnapshot();
  private checkpoint: MiningCheckpoint | null = null;
  private checkpointRevision: number | null = null;
  private checkpointWrite: Promise<void> = Promise.resolve();
  private configurationGeneration = 0;
  private readonly retiredProofs = new Map<string, { proofId: string; status: "EXPIRED" | "CONSUMED" }>();
  private backend: MiningBackend | null = null;
  private abortController: AbortController | null = null;
  private loopPromise: Promise<void> | null = null;
  private startPromise: Promise<void> | null = null;
  private pausePromise: Promise<void> | null = null;
  private startupAbort: AbortController | null = null;
  private submissionPromise: Promise<void> | null = null;
  private submissionAbort: AbortController | null = null;
  private executionGeneration = 0;
  private pauseRevision = 0;
  private executionGuard: MiningStartOptions["guard"];
  private runningSinceMs: number | null = null;
  private rateSamples: Array<{ attempts: number; elapsedMs: number }> = [];
  private rateSampleAtMs = 0;
  private disposed = false;
  private disposalPromise: Promise<void> | null = null;
  private readonly autoPauseWhenHidden: boolean;
  private readonly visibilityListener: (() => void) | null;

  constructor(options: MiningControllerOptions) {
    this.api = options.api;
    this.server = options.server ? new ServerMiningLease(options.server.api, options.server.auth, options.server.allocationPolicy,
      reason => {
        this.pause(reason).catch(() => console.error("Mining lease cleanup failed."));
        if (this.snapshot.session) this.update({ session: { ...this.snapshot.session, lease: null,
          message: reason === "MINING_MOVED" ? "Mining moved to another tab or browser. Check status to continue here." :
            "Mining paused. Check execution access before resuming." } });
      }, options.server.workerId) : null;
    if (this.server) this.snapshot = { ...this.snapshot, session: { status: "UNKNOWN", lease: null,
      deploymentReady: this.server.allocationPolicy !== null, message: null }, savedResults: [] };
    this.store = options.checkpointStore ?? new IndexedDbMiningCheckpointStore();
    this.lease = options.tabLease ?? new BrowserMiningTabLease();
    this.nowMs = options.nowMs ?? (() => Date.now());
    this.factories = options.backends ?? {
      cpu: createCpuWorkerBackend,
      gpu: (onCapability) => createGpuWorkerBackend(onCapability),
    };
    this.autoPauseWhenHidden = options.autoPauseWhenHidden === true;
    this.visibilityListener = !this.autoPauseWhenHidden || typeof document === "undefined" ? null : () => {
      if (document.visibilityState === "hidden" &&
        (this.snapshot.phase === "RUNNING" || this.snapshot.phase === "ACQUIRING_TAB_LOCK")) {
        this.pause("PAGE_HIDDEN").catch((error: unknown) => this.fail(error, "MINING_PAUSE_FAILED"));
      }
    };
    if (this.visibilityListener) document.addEventListener("visibilitychange", this.visibilityListener);
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): MiningSnapshot => this.snapshot;

  async discover(token: string, guard: NonNullable<MiningStartOptions["guard"]>): Promise<void> {
    if (!this.server) return;
    const operation = ++this.discovery;
    const current = () => { guard.signal.throwIfAborted(); guard.assertCurrent();
      if (operation !== this.discovery || this.disposed) throw new DOMException("Obsolete discovery", "AbortError"); };
    current();
    this.update({ session: { ...this.snapshot.session!, status: "CHECKING", message: "Checking your saved mining session." } });
    try {
      const result = await this.server.current(guard.signal);
      current();
      const records = await this.store.list();
      current();
      const identity = this.server.identity();
      const instance = "challenge" in result ? result.challenge?.instanceId : this.snapshot.challenge?.instanceId;
      const savedResults = records.filter(row => (row.proof || row.candidate) && row.identity.walletAddress.toLowerCase() === identity.walletAddress.toLowerCase() &&
        row.identity.chainId === String(identity.chainId) && row.identity.tokenAddress.toLowerCase() === token.toLowerCase() &&
        (!instance || row.identity.instanceId.toLowerCase() === instance.toLowerCase()))
        .map(row => ({ proofId: row.proof?.proofId ?? null, challengeId: row.identity.challengeId }));
      if (result.status === "LEGACY_CONFLICT") {
        await this.pause("MINING_LEGACY_CONFLICT"); current();
        this.update({ session: { ...this.snapshot.session!, status: result.status, lease: null,
          message: "Older mining sessions need resolution before you can resume." }, savedResults });
        return;
      }
      if (result.status === "NONE") {
        this.update({ session: { ...this.snapshot.session!, status: "NONE", lease: null, message: null }, savedResults });
        return;
      }
      if (result.challenge.tokenAddress.toLowerCase() !== token.toLowerCase()) {
        await this.pause("MINING_SOURCE_CONFLICT"); current();
        this.update({ session: { ...this.snapshot.session!, status: result.status, lease: result.lease?.state ?? null,
          message: "This wallet has a mining session for another source token. Select that token to continue." }, savedResults });
        return;
      }
      // A background read of the same running challenge must not reset workers/telemetry.
      const same = this.snapshot.challenge?.challengeId.toLowerCase() === result.challenge.challengeId.toLowerCase();
      const executing = ["RUNNING", "ACQUIRING_TAB_LOCK", "CANDIDATE_LOCAL", "SUBMITTING"].includes(this.snapshot.phase);
      if (!same || !executing) {
        await this.configure({ challenge: result.challenge, guard }, true); current();
        if (result.proof) await this.retainReadProof(result.proof, current);
        current();
        if (this.snapshot.phase === "CHALLENGE_READY") this.update({ phase: "PAUSED", pauseReason: "RESTORED_CHECKPOINT" });
      }
      this.update({ session: { ...this.snapshot.session!, status: result.status, lease: result.lease?.state ?? null, message: null }, savedResults });
    } catch (error) {
      current();
      this.update({ session: { ...this.snapshot.session!, status: "UNKNOWN", message: "Mining status is unavailable. Check your connection and retry." } });
      throw error;
    }
  }

  private retainReadProof(proof: MiningProof, assertCurrent: () => void): Promise<void> {
    if (this.recoveryPromise) return this.recoveryPromise;
    const pending = this.retainReadProofInternal(proof, assertCurrent);
    this.recoveryPromise = pending;
    void pending.finally(() => { if (this.recoveryPromise === pending) this.recoveryPromise = null; }).catch(() => undefined);
    return pending;
  }

  private async retainReadProofInternal(proof: MiningProof, assertCurrent: () => void): Promise<void> {
    if (!this.checkpoint || !this.snapshot.challenge) throw new Error("MINING_CHECKPOINT_MISSING");
    const key = this.checkpoint.key;
    if (!await this.lease.acquire(key)) throw new Error("MINING_TAB_LEASE_LOST");
    try {
      await this.reloadDurableCheckpointAfterLease(assertCurrent); assertCurrent();
      verifyPowCandidate(this.snapshot.challenge!.challengeCommitment, proof.nonce, this.snapshot.challenge!.targetExclusive, proof.digest);
      if (this.snapshot.candidate && this.snapshot.candidate.nonce !== proof.nonce) this.archiveCandidate();
      if (this.snapshot.candidate?.nonce !== proof.nonce) delete this.checkpoint!.candidateProvenance;
      const candidate = { nonce: proof.nonce, digest: proof.digest, backend: this.snapshot.mode, foundAtMs: proof.acceptedAt * 1_000 };
      this.update({ candidate });
      await this.persistCheckpoint(proof); assertCurrent();
      this.update({ proof, phase: phaseForChallenge(this.snapshot.challenge!, this.checkpoint!, this.nowMs()) });
    } finally { await this.lease.release(); }
  }

  async createAttempt(token: string, guard: NonNullable<MiningStartOptions["guard"]>): Promise<void> {
    if (!this.server) throw new Error("MINING_SERVER_SESSION_REQUIRED");
    await this.discover(token, guard);
    guard.assertCurrent();
    if (["ACTIVE", "LEGACY_CONFLICT", "UNKNOWN", "CHECKING"].includes(this.snapshot.session!.status) || this.snapshot.session!.message)
      throw new Error("MINING_CURRENT_REQUIRES_RESOLUTION");
    const challenge = await this.server.create(token, guard.signal);
    guard.assertCurrent();
    await this.configure({ challenge, guard });
    this.update({ session: { ...this.snapshot.session!, status: challenge.status, lease: "AVAILABLE", message: null } });
  }

  async checkSavedProof(id: Hex, guard: NonNullable<MiningStartOptions["guard"]>, token?: string): Promise<void> {
    if (!this.server) throw new Error("MINING_SERVER_SESSION_REQUIRED");
    if (["RUNNING", "ACQUIRING_TAB_LOCK", "CANDIDATE_LOCAL", "SUBMITTING"].includes(this.snapshot.phase)) throw new Error("PAUSE_BEFORE_PROOF_RECOVERY");
    const records = await this.store.list(); guard.assertCurrent();
    const identity = this.server.identity();
    const selected = this.snapshot.challenge;
    const record = records.find(row => (row.proof?.proofId === id || row.identity.challengeId === id) && row.identity.walletAddress.toLowerCase() === identity.walletAddress.toLowerCase() &&
      row.identity.chainId === String(identity.chainId) && (!token || row.identity.tokenAddress.toLowerCase() === token.toLowerCase()) &&
      (!selected || row.identity.tokenAddress.toLowerCase() === selected.tokenAddress.toLowerCase() &&
        row.identity.instanceId.toLowerCase() === selected.instanceId.toLowerCase()));
    if (!record) throw new Error("SAVED_PROOF_NOT_FOUND");
    const challenge = await this.server.getChallenge(record.identity.challengeId, guard.signal); guard.assertCurrent();
    if (!checkpointMatches(record, normalizeChallenge(challenge))) throw new Error("MINING_CHECKPOINT_IDENTITY_MISMATCH");
    const proof = record.proof ? await this.server.api.getProof(record.proof.proofId, challenge, guard.signal) : null;
    guard.assertCurrent();
    await this.configure({ challenge, guard });
    if (proof) await this.retainReadProof(proof, guard.assertCurrent);
    guard.assertCurrent();
    // Selecting the first saved result after NONE establishes its display scope.
    // Keep all local keys in that scope available for the next explicit check.
    this.update({ savedResults: records.filter(row => (row.proof || row.candidate) &&
      row.identity.walletAddress.toLowerCase() === record.identity.walletAddress.toLowerCase() &&
      row.identity.chainId === record.identity.chainId && row.identity.tokenAddress.toLowerCase() === record.identity.tokenAddress.toLowerCase() &&
      row.identity.instanceId.toLowerCase() === record.identity.instanceId.toLowerCase())
      .map(row => ({ proofId: row.proof?.proofId ?? null, challengeId: row.identity.challengeId })) });
  }

  async configure(configuration: MiningControllerConfiguration, preserveLocalSettings = false): Promise<void> {
    this.assertUsable();
    if (configuration.api) this.api = configuration.api;
    if (!configuration.challenge && !configuration.challengeId) return;
    const generation = ++this.configurationGeneration;
    const assertCurrent = () => {
      this.assertUsable();
      configuration.guard?.signal.throwIfAborted();
      configuration.guard?.assertCurrent();
      if (generation !== this.configurationGeneration) throw new Error("MINING_CONFIGURATION_CHANGED");
    };
    await this.quiesce("CHALLENGE_CHANGED");
    assertCurrent();
    const wire = configuration.challenge ?? await this.api.getChallenge(configuration.challengeId!, configuration.guard?.signal);
    assertCurrent();
    const challenge = normalizeChallenge(wire);
    const key = challengeCheckpointKey(challenge);
    let restored = await this.store.load(key);
    assertCurrent();
    // Configure can only insert an absent row. It cannot overwrite progress,
    // even when another tab creates the same checkpoint during the await.
    if (!restored) {
      const initial = freshCheckpoint(challenge, this.snapshot.mode, this.snapshot.powerMode, this.nowMs());
      try { await this.store.save(initial, { expectedRevision: null }); restored = initial; }
      catch (error) {
        if (!(error instanceof Error) || error.message !== "MINING_CHECKPOINT_VERSION_CHANGED") throw error;
        restored = await this.store.load(key);
      }
      assertCurrent();
    }
    if (restored && !checkpointMatches(restored, challenge)) throw new Error("MINING_CHECKPOINT_IDENTITY_MISMATCH");
    if (restored) validateRecoveredCheckpoint(restored, challenge);
    if (restored) this.applyKnownProofRetirement(restored);
    this.checkpoint = restored && checkpointMatches(restored, challenge) ? restored : freshCheckpoint(challenge,
      this.snapshot.mode, this.snapshot.powerMode, this.nowMs());
    this.checkpointRevision = restored ? restored.revision ?? 0 : null;
    const phase = phaseForChallenge(challenge, this.checkpoint, this.nowMs());
    const previous = this.snapshot.challenge;
    const sameContext = previous && previous.instanceId.toLowerCase() === challenge.instanceId.toLowerCase() &&
      previous.chainId === challenge.chainId && previous.walletAddress.toLowerCase() === challenge.walletAddress.toLowerCase() &&
      previous.tokenAddress.toLowerCase() === challenge.tokenAddress.toLowerCase();
    // Current-session reads restore progress, but must not undo settings selected
    // while paused. A first restore or different challenge still uses its checkpoint.
    const keepSettings = preserveLocalSettings && sameContext && previous.challengeId.toLowerCase() === challenge.challengeId.toLowerCase();
    this.update({
      ...(!sameContext ? { logs: [], estimatedTimeMs: null, savedResults: [] } : {}),
      phase,
      challenge,
      proof: this.checkpoint.proof,
      candidate: this.checkpoint.candidate,
      candidateCanRetry: !this.server || Boolean(this.checkpoint.candidateProvenance),
      attempts: BigInt(this.checkpoint.completedAttempts),
      attemptsText: this.checkpoint.completedAttempts,
      activeElapsedMs: this.checkpoint.activeElapsedMs,
      hashesPerSecond: null,
      mode: keepSettings ? this.snapshot.mode : this.checkpoint.mode,
      powerMode: keepSettings ? this.snapshot.powerMode : normalizeWorkload(this.checkpoint.powerMode),
      cpu: { ...this.snapshot.cpu!, selectedWorkers: keepSettings || this.checkpoint.cpuWorkerCount === undefined ?
        this.snapshot.cpu!.selectedWorkers : Math.min(this.checkpoint.cpuWorkerCount, this.snapshot.cpu!.workerLimit), activeWorkers: 0 },
      pauseReason: phase === "PAUSED" ? "RESTORED_CHECKPOINT" : null,
      errorCode: null,
      tabOwner: false,
    });
    this.log("CHALLENGE_CONFIGURED", challenge.challengeId);
    if (this.server) this.update({ session: { ...this.snapshot.session!, status: challenge.status, lease: null, message: null } });
  }

  async restore(context: { walletAddress: string; chainId: string; tokenAddress: string },
    guard: NonNullable<MiningStartOptions["guard"]>): Promise<boolean> {
    const assertCurrent = () => { this.assertUsable(); guard.signal.throwIfAborted(); guard.assertCurrent(); };
    assertCurrent();
    const checkpoints = await this.store.list();
    assertCurrent();
    const saved = checkpoints.filter((row) => row.version === 1 && row.identity.chainId === context.chainId &&
      row.identity.walletAddress.toLowerCase() === context.walletAddress.toLowerCase() &&
      row.identity.tokenAddress.toLowerCase() === context.tokenAddress.toLowerCase())
      .sort((left, right) => right.updatedAtMs - left.updatedAtMs);
    for (const row of saved) {
      // GET by a stored ID is the existing API authority. A failed/unknown read
      // propagates; it is never an excuse to issue a replacement challenge.
      const wire = await this.api.getChallenge(row.identity.challengeId, guard.signal);
      assertCurrent();
      const challenge = normalizeChallenge(wire);
      if (!checkpointMatches(row, challenge)) throw new Error("MINING_CHECKPOINT_IDENTITY_MISMATCH");
      if (!row.proof && !row.candidate && (challenge.status === "EXPIRED" || challenge.status === "CANCELLED")) continue;
      await this.configure({ challenge: wire, guard });
      assertCurrent();
      if (this.snapshot.phase === "CHALLENGE_READY") this.update({ phase: "PAUSED", pauseReason: "RESTORED_CHECKPOINT" });
      return true;
    }
    return false;
  }

  retireProof(proofId: string, status: "EXPIRED" | "CONSUMED"): void {
    if (this.snapshot.proof?.proofId.toLowerCase() !== proofId.toLowerCase()) return;
    // Retain recovery identity/candidate. No non-owner checkpoint write.
    const proof = { ...this.snapshot.proof, proofStatus: status };
    if (this.checkpoint) this.retiredProofs.set(this.checkpoint.key, { proofId, status });
    this.update({ proof, phase: status === "EXPIRED" ? "EXPIRED" : "CANCELLED",
      pauseReason: status === "EXPIRED" ? "PROOF_EXPIRED" : "PROOF_CONSUMED", hashesPerSecond: null });
  }

  start(options: MiningStartOptions = {}): Promise<void> {
    this.assertUsable();
    options.guard?.assertCurrent();
    if (this.startPromise) return this.startPromise;
    const abort = new AbortController();
    this.startupAbort = abort;
    const cancel = () => abort.abort(options.guard?.signal.reason);
    options.guard?.signal.addEventListener("abort", cancel, { once: true });
    if (options.guard?.signal.aborted) cancel();
    const pausing = this.pausePromise;
    const starting = (async () => {
      // PAUSED can be visible while the final save and lease release are still
      // pending. Never let their cleanup act on a new execution's resources.
      if (pausing) await pausing;
      await this.startInternal(options, abort.signal);
    })().catch(async (error: unknown) => {
      await this.releaseExecution();
      if (!abort.signal.aborted) {
        this.fail(error, "MINING_START_FAILED");
        throw error;
      }
      // Do not overwrite a saved proof/candidate or a terminal challenge state.
      if (["CHALLENGE_READY", "ACQUIRING_TAB_LOCK", "PAUSED"].includes(this.snapshot.phase)) {
        this.update({ phase: "PAUSED", pauseReason: options.guard?.signal.aborted ?
          "WALLET_CONTEXT_CHANGED" : this.snapshot.pauseReason ?? "START_CANCELLED", hashesPerSecond: null, tabOwner: false });
        // No batch has run. Do not overwrite a checkpoint another tab may have
        // advanced while this startup was waiting to acquire its lease.
      }
    });
    this.startPromise = starting;
    void starting.finally(() => {
      options.guard?.signal.removeEventListener("abort", cancel);
      if (this.startupAbort === abort) this.startupAbort = null;
      if (this.startPromise === starting) this.startPromise = null;
    }).catch(() => undefined);
    return starting;
  }

  private async startInternal(options: MiningStartOptions, signal: AbortSignal): Promise<void> {
    this.assertUsable();
    const assertStarting = () => { signal.throwIfAborted(); options.guard?.assertCurrent(); };
    assertStarting();
    if (options.mode) this.update({ mode: options.mode });
    if (options.powerMode !== undefined) this.update({ powerMode: normalizeWorkload(options.powerMode) });
    if (!this.snapshot.challenge || !this.checkpoint) throw new Error("MINING_CHALLENGE_NOT_CONFIGURED");
    if (this.snapshot.proof) return;
    if (this.snapshot.candidate && !this.server) { await this.submitCandidate(signal, assertStarting); return; }
    if (this.server && this.snapshot.candidate && !options.freshSearch) throw new Error("MINING_CANDIDATE_UNCONFIRMED");
    await this.refreshChallenge(signal, assertStarting);
    assertStarting();
    if (!this.snapshot.challenge) return;
    if (this.loopPromise) return;
    const generation = ++this.executionGeneration;
    this.update({ phase: "ACQUIRING_TAB_LOCK", errorCode: null, pauseReason: null });
    if (this.server) {
      const selected = await this.server.current(signal); assertStarting();
      if (selected.status !== "ACTIVE" || selected.challenge.challengeId !== this.snapshot.challenge.challengeId)
        throw new Error("MINING_CURRENT_REQUIRES_RESOLUTION");
      await this.server.acquire(this.snapshot.challenge, options.takeover === true, signal); assertStarting();
    }
    let acquired = await this.lease.acquire(this.checkpoint.key, signal);
    if (this.server && !acquired) {
      // A transferred same-origin owner may still be joining its workers. Wait
      // boundedly for its ordinary release, never delete its lock/checkpoint.
      for (let attempt = 0; !acquired && attempt < 48; attempt++) {
        await new Promise<void>(resolve => setTimeout(resolve, 250));
        assertStarting(); this.server.assert();
        acquired = await this.lease.acquire(this.checkpoint.key, signal);
      }
    }
    assertStarting();
    if (generation !== this.executionGeneration) {
      if (acquired) await this.lease.release();
      return;
    }
    if (!acquired) {
      await this.server?.release();
      this.update({ phase: "PAUSED", tabOwner: false, pauseReason: "ANOTHER_TAB_IS_MINING", hashesPerSecond: null });
      this.log("TAB_LOCK_DENIED");
      return;
    }
    this.update({ tabOwner: true });
    try {
      // A second tab can configure while the first tab is still mining. The
      // challenge lease serializes execution, but the checkpoint must also be
      // reloaded after the lease is acquired so the new owner cannot dispatch
      // a stale nonce range.
      await this.reloadDurableCheckpointAfterLease(assertStarting);
      assertStarting();
      if (this.snapshot.proof) {
        this.update({ phase: phaseForChallenge(this.snapshot.challenge!, this.checkpoint!, this.nowMs()),
          pauseReason: null, hashesPerSecond: null });
        await this.releaseExecution();
        return;
      }
      if (this.snapshot.candidate) {
        if (this.server && options.freshSearch) {
          this.archiveCandidate();
          this.update({ candidate: null, proof: null });
          delete this.checkpoint!.candidateProvenance;
          delete this.checkpoint!.allocation;
          await this.persistCheckpoint(); assertStarting();
        } else {
        this.update({ phase: "SUBMIT_RETRY", pauseReason: "RESTORED_CANDIDATE", hashesPerSecond: null });
        if (!this.server) await this.submitCandidate(signal, assertStarting);
        else throw new Error("MINING_CANDIDATE_UNCONFIRMED");
        return;
        }
      }
      if (this.snapshot.challenge.status !== "ACTIVE" ||
        this.nowSeconds() >= BigInt(this.snapshot.challenge.expiresAt)) {
        const phase = phaseForChallenge(this.snapshot.challenge, this.checkpoint!, this.nowMs());
        this.update({ phase, hashesPerSecond: null });
        await this.releaseExecution();
        return;
      }
      this.backend = this.snapshot.mode === "CPU" ? this.factories.cpu() :
        this.factories.gpu((gpu) => this.update({ gpu }));
      if (this.snapshot.mode === "GPU") this.update({ gpu: { ...this.snapshot.gpu, status: "CHECKING", reason: null } });
      await this.backend.initialize(this.snapshot.mode === "CPU" ? this.snapshot.cpu!.selectedWorkers : this.snapshot.powerMode);
      assertStarting();
      if (this.snapshot.mode === "CPU") this.update({ cpu: { ...this.snapshot.cpu!, activeWorkers: this.backend.workerCount ?? 1 } });
      if (generation !== this.executionGeneration ||
        (this.autoPauseWhenHidden && typeof document !== "undefined" && document.visibilityState === "hidden")) {
        await this.releaseExecution();
        if (generation === this.executionGeneration) {
          this.update({ phase: "PAUSED", pauseReason: "PAGE_HIDDEN", hashesPerSecond: null, tabOwner: false });
        }
        return;
      }
    } catch (error) {
      await this.releaseExecution();
      if (signal.aborted) throw error;
      if (generation !== this.executionGeneration) return;
      this.fail(error, this.snapshot.mode === "GPU" ? "GPU_INITIALIZATION_FAILED" : "CPU_INITIALIZATION_FAILED");
      return;
    }
    this.abortController = new AbortController();
    this.runningSinceMs = this.nowMs();
    this.rateSamples = [];
    this.rateSampleAtMs = this.runningSinceMs;
    this.executionGuard = options.guard;
    this.update({ phase: "RUNNING", pauseReason: null, errorCode: null, hashesPerSecond: null });
    this.log("MINING_STARTED", `${this.snapshot.mode}:${this.snapshot.powerMode}`);
    const loop = this.runLoop(this.abortController.signal, () => {
      options.guard?.signal.throwIfAborted(); options.guard?.assertCurrent();
    });
    this.loopPromise = loop;
    void loop.finally(() => { if (this.loopPromise === loop) this.loopPromise = null; }).catch(() => undefined);
  }

  pause(reason = "USER_PAUSED"): Promise<void> {
    this.pauseRevision++;
    // Startup may still be waiting for the challenge refresh while its visible
    // phase is CHALLENGE_READY/PAUSED. Invalidate it before checking that phase.
    this.startupAbort?.abort(new DOMException(reason, "AbortError"));
    this.server?.invalidate();
    // Fence synchronously, before candidate/checkpoint continuations or a new
    // valid cookie can dispatch an old execution's submission.
    this.executionGeneration++;
    this.abortController?.abort(new DOMException(reason, "AbortError"));
    this.submissionAbort?.abort(new DOMException(reason, "AbortError"));
    if (this.pausePromise) return this.pausePromise;
    const starting = this.startPromise;
    // Install the pause flight before publishing any state. Join only the
    // captured startup: a later Resume may itself be waiting for this pause.
    const pausing = Promise.resolve().then(() => this.pauseInternal(reason, starting));
    this.pausePromise = pausing;
    void pausing.finally(() => {
      if (this.pausePromise === pausing) this.pausePromise = null;
    }).catch(() => undefined);
    return pausing;
  }

  private async pauseInternal(reason: string, starting: Promise<void> | null): Promise<void> {
    if (starting && this.snapshot.phase !== "RUNNING" && this.snapshot.phase !== "ACQUIRING_TAB_LOCK") {
      await starting.catch(() => undefined);
      return;
    }
    if (!["RUNNING", "ACQUIRING_TAB_LOCK", "CANDIDATE_LOCAL", "SUBMITTING"].includes(this.snapshot.phase) &&
      !this.submissionPromise && !this.loopPromise) return;
    const wasRunning = this.snapshot.phase === "RUNNING";
    this.executionGeneration++;
    this.captureElapsed();
    this.abortController?.abort();
    const loop = this.loopPromise;
    const submission = this.submissionPromise;
    if (loop) await loop.catch(() => undefined);
    if (submission) await submission.catch(() => undefined);
    try {
      if (this.snapshot.phase === "RUNNING" || this.snapshot.phase === "ACQUIRING_TAB_LOCK") {
        this.update({ phase: "PAUSED", pauseReason: reason, hashesPerSecond: null });
        if (wasRunning) await this.persistCheckpoint();
        this.log("MINING_PAUSED", reason);
      }
    } finally { await this.releaseExecution(); }
    if (starting) await starting.catch(() => undefined);
  }

  async resume(options: MiningStartOptions = {}): Promise<void> {
    await this.start(options);
  }

  async stop(): Promise<void> {
    await this.pause("USER_STOPPED");
    if (this.snapshot.candidate || this.snapshot.phase === "SUBMITTING" || this.snapshot.phase === "SUBMIT_RETRY") return;
    if (this.snapshot.challenge && this.snapshot.phase !== "VERIFIED" && this.snapshot.phase !== "EXPIRED" &&
      this.snapshot.phase !== "CANCELLED") {
      this.update({ phase: "CHALLENGE_READY", pauseReason: "USER_STOPPED", hashesPerSecond: null });
    }
  }

  async retrySubmission(options: MiningStartOptions = {}): Promise<void> {
    this.assertUsable();
    if (this.pausePromise) await this.pausePromise;
    options.guard?.assertCurrent();
    if (!this.snapshot.candidate) throw new Error("NO_CANDIDATE_TO_RETRY");
    await this.submitCandidate(options.guard?.signal, options.guard?.assertCurrent.bind(options.guard));
  }

  async setMode(mode: MiningMode): Promise<void> {
    await this.changeSettings("MODE_CHANGED", () => this.update({ mode, hashesPerSecond: null }));
  }

  async setPowerMode(powerMode: MiningPowerMode): Promise<void> {
    this.assertUsable();
    powerMode = normalizeWorkload(powerMode);
    await this.changeSettings("POWER_MODE_CHANGED", () => this.update({ powerMode, hashesPerSecond: null }));
  }

  async setCpuWorkers(count: number): Promise<void> {
    this.assertUsable();
    validateCpuWorkers(count, this.snapshot.cpu!.workerLimit);
    await this.changeSettings("CPU_WORKERS_CHANGED", () => this.update({ cpu: { ...this.snapshot.cpu!, selectedWorkers: count } }));
  }

  private async changeSettings(reason: string, apply: () => void): Promise<void> {
    this.assertUsable();
    const wasRunning = this.snapshot.phase === "RUNNING";
    const guard = this.executionGuard;
    const configuration = this.configurationGeneration;
    const pausing = wasRunning || this.startPromise ? this.pause(reason) : null;
    const revision = this.pauseRevision;
    if (pausing) await pausing;
    this.assertUsable();
    // A later Pause cancels this restart intent even if identity stayed the same.
    if (revision !== this.pauseRevision || configuration !== this.configurationGeneration) return;
    if (wasRunning) { guard?.signal.throwIfAborted(); guard?.assertCurrent(); }
    // Spectator preferences stay local until execution is explicitly acquired.
    apply();
    if (!wasRunning || revision !== this.pauseRevision || configuration !== this.configurationGeneration) return;
    guard?.signal.throwIfAborted(); guard?.assertCurrent();
    await this.start(guard ? { guard } : {});
  }

  dispose(): Promise<void> {
    if (this.disposalPromise) return this.disposalPromise;
    this.disposed = true;
    if (this.visibilityListener) document.removeEventListener("visibilitychange", this.visibilityListener);
    this.listeners.clear();
    this.disposalPromise = (async () => {
      try { await this.quiesce("CONTROLLER_DISPOSED"); }
      finally {
        this.server?.dispose();
        // A checkpoint failure must not leave a worker or tab lease alive.
        try { await this.releaseExecution(); }
        finally { await this.lease.dispose(); }
      }
    })();
    return this.disposalPromise;
  }

  private async runLoop(signal: AbortSignal, assertContext: () => void = () => {}): Promise<void> {
    const backend = this.backend!;
    const executionFence = this.lease.fence(this.checkpoint!.key);
    let unpersistedProgress = false;
    try {
      while (!signal.aborted && this.snapshot.phase === "RUNNING") {
        assertContext();
        if (!executionFence?.isCurrent() || !this.lease.held()) throw new Error("MINING_TAB_LEASE_LOST");
        const challenge = this.snapshot.challenge!;
        if (this.nowSeconds() >= BigInt(challenge.expiresAt)) {
          this.captureElapsed();
          this.update({ phase: "EXPIRED", pauseReason: "CHALLENGE_EXPIRED", hashesPerSecond: null });
          await this.persistCheckpoint();
          break;
        }
        const batch = await this.reserveBatch(backend.preferredBatchSize ?? batchSize(this.snapshot.mode, this.snapshot.powerMode), signal);
        signal.throwIfAborted();
        if (this.server) { await this.server.beforeDispatch(); signal.throwIfAborted(); }
        assertContext();
        if (!this.lease.held()) throw new Error("MINING_TAB_LEASE_LOST");
        let reported = 0;
        let acceptingProgress = true;
        const progress = (completed: number) => {
          if (!acceptingProgress) return;
          if (!executionFence?.isCurrent() || !this.lease.held()) throw new Error("MINING_TAB_LEASE_LOST");
          if (!Number.isSafeInteger(completed) || completed < reported || completed > batch.count) {
            throw new Error("INVALID_MINING_BATCH_RESULT");
          }
          if (completed > reported) { this.recordProgress(completed - reported); unpersistedProgress = true; }
          reported = completed;
          if (this.nowSeconds() >= BigInt(challenge.expiresAt)) this.abortController?.abort();
        };
        let result: BatchResult;
        try {
          result = await backend.runBatch({
            ...(this.server ? { deadline: this.server.deadline() } : {}),
            commitment: challenge.challengeCommitment,
            targetExclusive: challenge.targetExclusive,
            startNonce: batch.start.toString(),
            count: batch.count,
          }, signal, progress);
          progress(result.completed);
        } finally { acceptingProgress = false; }
        unpersistedProgress = false;
        await this.persistCheckpoint();
        if (result.candidate) {
          const nonce = BigInt(result.candidate.nonce);
          if (nonce < batch.start || nonce >= batch.start + BigInt(batch.count)) throw new Error("MINING_CANDIDATE_OUTSIDE_BATCH");
          const digest = verifyPowCandidate(challenge.challengeCommitment, result.candidate.nonce,
            challenge.targetExclusive, result.candidate.digest);
          const candidate: LocalPowCandidate = { nonce: result.candidate.nonce, digest,
            backend: this.snapshot.mode, foundAtMs: this.nowMs() };
          this.checkpoint!.candidate = candidate;
          if (this.checkpoint!.allocation) this.checkpoint!.candidateProvenance = structuredClone(this.checkpoint!.allocation);
          this.captureElapsed();
          this.update({ phase: "CANDIDATE_LOCAL", candidate, candidateCanRetry: !this.server || Boolean(this.checkpoint!.candidateProvenance), hashesPerSecond: null });
          await this.persistCheckpoint();
          this.log("LOCAL_CANDIDATE_VERIFIED", candidate.nonce);
          if (signal.aborted) {
            this.update({ phase: "SUBMIT_RETRY", pauseReason: "SAVED_CANDIDATE", hashesPerSecond: null });
          } else await this.submitCandidate(signal, () => { assertContext(); this.server?.assert(); });
          break;
        }
        if (this.nowSeconds() >= BigInt(challenge.expiresAt)) {
          this.captureElapsed();
          this.update({ phase: "EXPIRED", pauseReason: "CHALLENGE_EXPIRED", hashesPerSecond: null });
          await this.persistCheckpoint();
          break;
        }
      }
    } catch (error) {
      if (!isAbort(error)) {
        this.fail(error, "MINING_BACKEND_FAILED");
        if (unpersistedProgress && executionFence?.isCurrent() && this.lease.held()) {
          try { await this.persistCheckpoint(); }
          catch (saveError) { this.fail(saveError, "MINING_CHECKPOINT_FAILED"); }
        }
      }
    } finally {
      // Submission owns its own release. Its late loop cleanup must not release
      // a lease a subsequent explicit retry has already acquired.
      if (this.snapshot.phase !== "RUNNING" && this.backend === backend) await this.releaseExecution();
    }
  }

  private submitCandidate(signal?: AbortSignal, assertCurrent: () => void = () => {}): Promise<void> {
    if (this.submissionPromise) return this.submissionPromise;
    const abort = new AbortController();
    this.submissionAbort = abort;
    const cancel = () => abort.abort(signal?.reason);
    signal?.addEventListener("abort", cancel, { once: true });
    if (signal?.aborted) cancel();
    const execution = this.executionGeneration;
    const configuration = this.configurationGeneration;
    const assertSubmitting = () => {
      abort.signal.throwIfAborted();
      assertCurrent();
      if (execution !== this.executionGeneration || configuration !== this.configurationGeneration)
        throw new DOMException("Mining context changed", "AbortError");
    };
    const submitting = this.submitCandidateInternal(abort.signal, assertSubmitting);
    this.submissionPromise = submitting;
    void submitting.finally(() => {
      signal?.removeEventListener("abort", cancel);
      if (this.submissionAbort === abort) this.submissionAbort = null;
      if (this.submissionPromise === submitting) this.submissionPromise = null;
    }).catch(() => undefined);
    return submitting;
  }

  private async submitCandidateInternal(signal: AbortSignal | undefined, assertCurrent: () => void): Promise<void> {
    if (!this.checkpoint) throw new Error("MINING_CHECKPOINT_MISSING");
    signal?.throwIfAborted();
    assertCurrent();
    const acquired = await this.lease.acquire(this.checkpoint.key, signal);
    if (!acquired) {
      this.update({ phase: "SUBMIT_RETRY", pauseReason: "ANOTHER_TAB_IS_MINING", tabOwner: false });
      return;
    }
    this.update({ tabOwner: true });
    try {
      signal?.throwIfAborted();
      assertCurrent();
      await this.reloadDurableCheckpointAfterLease(() => { signal?.throwIfAborted(); assertCurrent(); });
      signal?.throwIfAborted();
      assertCurrent();
      if (this.snapshot.proof) {
        this.update({ phase: phaseForChallenge(this.snapshot.challenge!, this.checkpoint, this.nowMs()),
          hashesPerSecond: null, pauseReason: null });
        return;
      }
      const challenge = this.snapshot.challenge;
      const candidate = this.snapshot.candidate;
      if (!challenge || !candidate || !this.checkpoint) throw new Error("NO_CANDIDATE_TO_SUBMIT");
      verifyPowCandidate(challenge.challengeCommitment, candidate.nonce, challenge.targetExclusive, candidate.digest);
      signal?.throwIfAborted();
      assertCurrent();
      this.update({ phase: "SUBMITTING", hashesPerSecond: null, errorCode: null });
      this.log("PROOF_SUBMITTING", candidate.nonce);
      let acceptedProof: MiningProof | undefined;
      try {
        signal?.throwIfAborted();
        assertCurrent();
        const provenance = this.checkpoint.candidateProvenance;
        if (this.server && !provenance) throw new Error("MINING_LEGACY_CANDIDATE_UNCONFIRMED");
        const proof = this.server ? await this.server.submit(challenge, candidate.nonce, provenance!, signal) :
          await this.api.submitProof(challenge.challengeId, candidate.nonce, signal);
        signal?.throwIfAborted();
        assertCurrent();
        if (proof.challengeId.toLowerCase() !== challenge.challengeId.toLowerCase() || proof.nonce !== candidate.nonce ||
          proof.digest.toLowerCase() !== candidate.digest.toLowerCase()) throw new Error("SERVER_PROOF_IDENTITY_MISMATCH");
        acceptedProof = proof;
        // Retain the accepted proof ID before publishing. If auth changes during
        // storage, keep this original-context record without a VERIFIED transition.
        await this.persistCheckpoint(proof);
        signal?.throwIfAborted();
        assertCurrent();
        this.update({ phase: phaseForChallenge(challenge, this.checkpoint, this.nowMs()), proof, candidate,
          pauseReason: null, errorCode: null, hashesPerSecond: null });
        this.log("PROOF_VERIFIED", proof.proofId);
      } catch (error) {
        this.update({ phase: "SUBMIT_RETRY", errorCode: safeErrorCode(error), pauseReason: "SUBMISSION_UNCONFIRMED",
          hashesPerSecond: null });
        await this.persistCheckpoint(acceptedProof);
        this.log("PROOF_SUBMIT_UNCONFIRMED", safeErrorCode(error));
      }
    } catch (error) {
      if (isAbort(error) && this.snapshot.candidate && !this.snapshot.proof) {
        this.update({ phase: "SUBMIT_RETRY", pauseReason: "SUBMISSION_UNCONFIRMED", hashesPerSecond: null });
      }
      throw error;
    } finally { await this.releaseExecution(); }
  }

  private async refreshChallenge(signal: AbortSignal, assertCurrent: () => void): Promise<void> {
    const current = this.snapshot.challenge;
    if (!current) return;
    const wire = await this.api.getChallenge(current.challengeId, signal);
    assertCurrent();
    const refreshed = normalizeChallenge(wire);
    if (!sameChallengeIdentity(current, refreshed)) throw new Error("SERVER_CHALLENGE_IDENTITY_CHANGED");
    this.update({ challenge: refreshed });
    if (refreshed.status === "EXPIRED") this.update({ phase: "EXPIRED", pauseReason: "CHALLENGE_EXPIRED" });
    else if (refreshed.status === "CANCELLED") this.update({ phase: "CANCELLED", pauseReason: "CHALLENGE_CANCELLED" });
    else if (refreshed.status === "SOLVED" && !this.snapshot.proof && !this.snapshot.candidate) {
      this.update({ phase: "SUBMIT_RETRY", pauseReason: "SERVER_SOLVED_PROOF_NEEDS_RECOVERY",
        errorCode: "SOLVED_PROOF_ID_UNKNOWN" });
    }
  }

  private async reloadDurableCheckpointAfterLease(assertCurrent: () => void): Promise<void> {
    const challenge = this.snapshot.challenge;
    const current = this.checkpoint;
    if (!challenge || !current) throw new Error("MINING_CHECKPOINT_MISSING");
    const durable = await this.store.load(current.key);
    assertCurrent();
    if (!this.lease.fence(current.key)) throw new Error("MINING_TAB_LEASE_LOST");
    if (!durable) {
      this.checkpointRevision = null;
      return;
    }
    if (!checkpointMatches(durable, challenge)) throw new Error("MINING_CHECKPOINT_IDENTITY_MISMATCH");
    validateRecoveredCheckpoint(durable, challenge);
    this.applyKnownProofRetirement(durable);
    this.checkpoint = durable;
    this.checkpointRevision = durable.revision ?? 0;
    const attempts = BigInt(durable.completedAttempts);
    this.update({
      candidate: durable.candidate,
      candidateCanRetry: !this.server || Boolean(durable.candidateProvenance),
      proof: durable.proof,
      attempts,
      attemptsText: attempts.toString(),
      activeElapsedMs: durable.activeElapsedMs,
      hashesPerSecond: null,
    });
  }

  private archiveCandidate(): void {
    if (!this.checkpoint || !this.snapshot.candidate) return;
    const old = { candidate: this.snapshot.candidate,
      ...(this.checkpoint.candidateProvenance ? { provenance: this.checkpoint.candidateProvenance } : {}) };
    this.checkpoint.unconfirmedCandidates = [...this.checkpoint.unconfirmedCandidates ?? [], structuredClone(old)];
    this.checkpoint.candidate = null;
  }

  private async reserveBatch(requested: number, signal: AbortSignal): Promise<{ start: bigint; count: number }> {
    if (!this.checkpoint) throw new Error("MINING_CHECKPOINT_MISSING");
    if (this.server) {
      await this.server.beforeDispatch(); signal.throwIfAborted();
      const allocation = this.checkpoint.allocation;
      if (!allocation || !this.server.matches(allocation) ||
        BigInt(this.checkpoint.reservedThroughExclusive) >= BigInt(allocation.endNonceExclusive)) {
        this.checkpoint.allocation = await this.server.allocate(signal, this.snapshot.hashesPerSecond);
        signal.throwIfAborted();
        this.checkpoint.reservedThroughExclusive = this.checkpoint.allocation.startNonce;
      }
      const end = BigInt(this.checkpoint.allocation!.endNonceExclusive);
      const start = BigInt(this.checkpoint.reservedThroughExclusive);
      const count = Number(end - start < BigInt(requested) ? end - start : BigInt(requested));
      if (count < 1 || start < BigInt(this.checkpoint.allocation!.startNonce)) throw new Error("MINING_ALLOCATION_INVALID");
      this.checkpoint.reservedThroughExclusive = (start + BigInt(count)).toString();
      await this.persistCheckpoint(); signal.throwIfAborted(); this.server.assert();
      return { start, count };
    }
    const start = BigInt(this.checkpoint.reservedThroughExclusive);
    if (start > MAX_UINT256) throw new Error("POW_NONCE_SPACE_EXHAUSTED");
    const remaining = MAX_UINT256 - start + 1n;
    const count = Number(remaining < BigInt(requested) ? remaining : BigInt(requested));
    const through = start + BigInt(count);
    this.checkpoint.reservedThroughExclusive = through.toString();
    this.checkpoint.mode = this.snapshot.mode;
    this.checkpoint.powerMode = this.snapshot.powerMode;
    this.checkpoint.cpuWorkerCount = this.snapshot.cpu!.selectedWorkers;
    // Persist before dispatch. A crash may skip a reserved tail but can never
    // repeat a nonce range after restart or a CPU/GPU mode switch.
    await this.persistCheckpoint();
    return { start, count };
  }

  private recordProgress(completed: number): void {
    if (!this.checkpoint || !Number.isSafeInteger(completed) || completed < 0) {
      throw new Error("INVALID_MINING_BATCH_RESULT");
    }
    const attempts = BigInt(this.checkpoint.completedAttempts) + BigInt(completed);
    this.checkpoint.completedAttempts = attempts.toString();
    this.captureElapsed(false);
    // Include checkpointing, dispatch and worker communication between batches.
    const sampledAtMs = this.nowMs();
    this.rateSamples.push({ attempts: completed, elapsedMs: Math.max(0, sampledAtMs - this.rateSampleAtMs) });
    this.rateSampleAtMs = sampledAtMs;
    while (this.rateSamples.length > 8) this.rateSamples.shift();
    const sampleAttempts = this.rateSamples.reduce((sum, sample) => sum + sample.attempts, 0);
    const sampleMs = this.rateSamples.reduce((sum, sample) => sum + sample.elapsedMs, 0);
    const rate = sampleMs > 0 ? sampleAttempts * 1_000 / sampleMs : null;
    this.update({ attempts, attemptsText: attempts.toString(),
      activeElapsedMs: this.currentElapsedMs(), hashesPerSecond: rate,
      estimatedTimeMs: estimateMiningTimeMs(this.snapshot.challenge?.targetExclusive ?? null, rate) ?? this.snapshot.estimatedTimeMs ?? null });
  }

  private captureElapsed(clear = true): void {
    if (!this.checkpoint || this.runningSinceMs === null) return;
    this.checkpoint.activeElapsedMs += Math.max(0, this.nowMs() - this.runningSinceMs);
    this.runningSinceMs = clear ? null : this.nowMs();
    this.update({ activeElapsedMs: this.checkpoint.activeElapsedMs });
  }

  private applyKnownProofRetirement(checkpoint: MiningCheckpoint): void {
    const retired = this.retiredProofs.get(checkpoint.key);
    if (retired && checkpoint.proof?.proofId.toLowerCase() === retired.proofId.toLowerCase()) {
      checkpoint.proof = { ...checkpoint.proof, proofStatus: retired.status };
    }
  }

  private currentElapsedMs(): number {
    return (this.checkpoint?.activeElapsedMs ?? 0) +
      (this.runningSinceMs === null ? 0 : Math.max(0, this.nowMs() - this.runningSinceMs));
  }

  private persistCheckpoint(acceptedProof?: MiningProof): Promise<void> {
    const write = this.checkpointWrite.then(() => this.persistCheckpointInternal(acceptedProof));
    this.checkpointWrite = write.catch(() => undefined);
    return write;
  }

  private async persistCheckpointInternal(acceptedProof?: MiningProof): Promise<void> {
    if (!this.checkpoint) return;
    const fence = this.lease.fence(this.checkpoint.key);
    if (!fence) throw new Error("MINING_TAB_LEASE_LOST");
    this.checkpoint.activeElapsedMs = this.currentElapsedMs();
    // Reset the running anchor after folding it into the durable total.
    if (this.runningSinceMs !== null) this.runningSinceMs = this.nowMs();
    this.checkpoint.mode = this.snapshot.mode;
    this.checkpoint.powerMode = this.snapshot.powerMode;
    this.checkpoint.cpuWorkerCount = this.snapshot.cpu!.selectedWorkers;
    this.checkpoint.candidate = this.snapshot.candidate;
    this.checkpoint.proof = acceptedProof ?? this.snapshot.proof;
    this.checkpoint.updatedAtMs = this.nowMs();
    const checkpoint = structuredClone(this.checkpoint);
    await this.store.save(checkpoint, { expectedRevision: this.checkpointRevision, fence });
    this.checkpointRevision = checkpoint.revision ?? 0;
    this.checkpoint.revision = this.checkpointRevision;
  }

  private async releaseExecution(): Promise<void> {
    this.abortController?.abort();
    this.abortController = null;
    await this.checkpointWrite;
    try { await this.backend?.dispose(); }
    finally {
      this.backend = null;
      if (this.snapshot.cpu!.activeWorkers !== 0) this.update({ cpu: { ...this.snapshot.cpu!, activeWorkers: 0 } });
      await this.lease.release();
      await this.server?.release();
    }
    if (this.snapshot.tabOwner) this.update({ tabOwner: false });
  }

  private async quiesce(reason: string): Promise<void> {
    await this.pause(reason);
    this.executionGeneration++;
    this.abortController?.abort();
    const starting = this.startPromise;
    const loop = this.loopPromise;
    const submission = this.submissionPromise;
    const pending = [...new Set([starting, loop, submission, this.recoveryPromise].filter((value): value is Promise<void> => value !== null))];
    if (pending.length) await Promise.allSettled(pending);
    await this.releaseExecution();
  }

  private fail(error: unknown, fallback: string): void {
    const code = safeErrorCode(error) || fallback;
    this.captureElapsed();
    this.update({ phase: "ERROR", errorCode: code, pauseReason: null, hashesPerSecond: null });
    this.log(fallback, code);
  }

  private log(code: string, detail?: string): void {
    const entry = { atMs: this.nowMs(), code, ...(detail ? { detail } : {}) };
    this.update({ logs: [...this.snapshot.logs.slice(-(MAX_LOGS - 1)), entry] });
  }

  private update(patch: Partial<MiningSnapshot>): void {
    if (patch.phase && patch.phase !== "RUNNING") {
      const cpu = patch.cpu ?? this.snapshot.cpu!;
      if (cpu.activeWorkers !== 0) patch = { ...patch, cpu: { ...cpu, activeWorkers: 0 } };
    }
    this.snapshot = Object.freeze({ ...this.snapshot, ...patch });
    for (const listener of this.listeners) listener();
  }

  private nowSeconds(): bigint { return BigInt(Math.floor(this.nowMs() / 1_000)); }
  private assertUsable(): void { if (this.disposed) throw new Error("MINING_CONTROLLER_DISPOSED"); }
}

export function createMiningController(options: MiningControllerOptions): MiningController {
  return new BrowserMiningController(options);
}

function initialSnapshot(): MiningSnapshot {
  return Object.freeze({ phase: "IDLE", mode: "CPU", powerMode: 1, cpu: cpuCapacity(), challenge: null, proof: null,
    candidate: null, attempts: 0n, attemptsText: "0", hashesPerSecond: null, activeElapsedMs: 0,
    gpu: DEFAULT_GPU, tabOwner: false, pauseReason: null, errorCode: null, logs: [] });
}

function freshCheckpoint(challenge: MiningChallenge, mode: MiningMode, powerMode: MiningPowerMode,
  nowMs: number): MiningCheckpoint {
  return { version: 1, key: challengeCheckpointKey(challenge), identity: {
    instanceId: challenge.instanceId, chainId: challenge.chainId, walletAddress: challenge.walletAddress,
    tokenAddress: challenge.tokenAddress, challengeId: challenge.challengeId,
    challengeCommitment: challenge.challengeCommitment,
  }, reservedThroughExclusive: "0", completedAttempts: "0", activeElapsedMs: 0, mode, powerMode,
  candidate: null, proof: null, updatedAtMs: nowMs };
}

function checkpointMatches(checkpoint: MiningCheckpoint, challenge: MiningChallenge): boolean {
  return checkpoint.version === 1 && checkpoint.key === challengeCheckpointKey(challenge) &&
    checkpoint.identity.instanceId.toLowerCase() === challenge.instanceId.toLowerCase() &&
    checkpoint.identity.chainId === challenge.chainId &&
    checkpoint.identity.walletAddress.toLowerCase() === challenge.walletAddress.toLowerCase() &&
    checkpoint.identity.tokenAddress.toLowerCase() === challenge.tokenAddress.toLowerCase() &&
    checkpoint.identity.challengeId.toLowerCase() === challenge.challengeId.toLowerCase() &&
    checkpoint.identity.challengeCommitment.toLowerCase() === challenge.challengeCommitment.toLowerCase();
}

function sameChallengeIdentity(left: MiningChallenge, right: MiningChallenge): boolean {
  return challengeCheckpointKey(left) === challengeCheckpointKey(right) &&
    left.targetExclusive === right.targetExclusive && left.expiresAt === right.expiresAt &&
    left.seed.toLowerCase() === right.seed.toLowerCase() && left.difficultyEpochId === right.difficultyEpochId;
}

function validateRecoveredCheckpoint(checkpoint: MiningCheckpoint, challenge: MiningChallenge): void {
  const allocations = [checkpoint.allocation, checkpoint.candidateProvenance,
    ...(checkpoint.unconfirmedCandidates ?? []).map(record => record.provenance)];
  for (const allocation of allocations) if (allocation) {
    if (!/^[A-Za-z0-9_-]{8,128}$/.test(allocation.workerId) || !/^[A-Za-z0-9_-]{8,128}$/.test(allocation.requestId) ||
      !Number.isSafeInteger(allocation.generation) || allocation.generation < 1 ||
      !Number.isSafeInteger(allocation.sessionRevision) || allocation.sessionRevision < 0 ||
      !Number.isSafeInteger(allocation.size) || allocation.size < 1 || allocation.size > 100_000_000 ||
      !/^(0|[1-9][0-9]*)$/.test(allocation.startNonce) || !/^(0|[1-9][0-9]*)$/.test(allocation.endNonceExclusive) ||
      BigInt(allocation.endNonceExclusive) > MAX_UINT256 + 1n ||
      BigInt(allocation.endNonceExclusive) - BigInt(allocation.startNonce) !== BigInt(allocation.size)) throw new Error("INVALID_MINING_ALLOCATION");
  }
  for (const record of checkpoint.unconfirmedCandidates ?? []) verifyPowCandidate(challenge.challengeCommitment,
    record.candidate.nonce, challenge.targetExclusive, record.candidate.digest);
  if (!/^(0|[1-9][0-9]*)$/.test(checkpoint.reservedThroughExclusive) ||
    BigInt(checkpoint.reservedThroughExclusive) > MAX_UINT256 + 1n ||
    !/^(0|[1-9][0-9]*)$/.test(checkpoint.completedAttempts) ||
    !Number.isFinite(checkpoint.activeElapsedMs) || checkpoint.activeElapsedMs < 0 ||
    !Number.isFinite(checkpoint.updatedAtMs) || !["CPU", "GPU"].includes(checkpoint.mode) ||
    checkpoint.revision !== undefined && (!Number.isSafeInteger(checkpoint.revision) || checkpoint.revision < 0)) {
    throw new Error("INVALID_MINING_CHECKPOINT");
  }
  normalizeWorkload(checkpoint.powerMode);
  if (checkpoint.cpuWorkerCount !== undefined) validateCpuWorkers(checkpoint.cpuWorkerCount, MAX_CPU_WORKERS);
  if (checkpoint.candidate) verifyPowCandidate(challenge.challengeCommitment, checkpoint.candidate.nonce,
    challenge.targetExclusive, checkpoint.candidate.digest);
  const proof = checkpoint.proof;
  if (proof) {
    if (!/^0x[0-9a-fA-F]{64}$/.test(proof.proofId) ||
      proof.challengeId.toLowerCase() !== challenge.challengeId.toLowerCase() ||
      parseUint256(String(proof.targetExclusive)) !== BigInt(challenge.targetExclusive) ||
      parseUint256(String(proof.difficultyEpochId)) !== BigInt(challenge.difficultyEpochId) ||
      !["VERIFIED", "EXPIRED", "CONSUMED"].includes(proof.proofStatus) ||
      !Number.isSafeInteger(proof.expiresAt) || proof.expiresAt < 0 ||
      checkpoint.candidate && (proof.nonce !== checkpoint.candidate.nonce ||
        proof.digest.toLowerCase() !== checkpoint.candidate.digest.toLowerCase())) {
      throw new Error("MINING_CHECKPOINT_PROOF_IDENTITY_MISMATCH");
    }
    verifyPowCandidate(challenge.challengeCommitment, proof.nonce, challenge.targetExclusive, proof.digest);
  }
}

function phaseForChallenge(challenge: MiningChallenge, checkpoint: MiningCheckpoint, nowMs: number): MiningSnapshot["phase"] {
  if (checkpoint.proof) return checkpoint.proof.proofStatus === "EXPIRED" ? "EXPIRED" :
    checkpoint.proof.proofStatus === "CONSUMED" ? "CANCELLED" : "VERIFIED";
  if (checkpoint.candidate) return "SUBMIT_RETRY";
  if (challenge.status === "EXPIRED" || BigInt(Math.floor(nowMs / 1_000)) >= BigInt(challenge.expiresAt)) return "EXPIRED";
  if (challenge.status === "CANCELLED") return "CANCELLED";
  if (challenge.status === "SOLVED") return "SUBMIT_RETRY";
  return BigInt(checkpoint.completedAttempts) > 0n || BigInt(checkpoint.reservedThroughExclusive) > 0n ?
    "PAUSED" : "CHALLENGE_READY";
}

function batchSize(mode: MiningMode, power: MiningPowerMode): number {
  return (mode === "GPU" ? 4_096 : 256) * normalizeWorkload(power);
}

function safeErrorCode(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error);
  return raw.replace(/[^A-Z0-9_:. -]/gi, "_").slice(0, 160) || "UNKNOWN_ERROR";
}
function isAbort(error: unknown): boolean { return error instanceof DOMException && error.name === "AbortError"; }
